import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { Device, DeviceCapability, DeviceModel, SimService } from '../lib/types'
import { Button, Field, Modal, toast } from './ui'

/**
 * Everything a fitter or support person may need to change about a unit
 * after it is registered: the hardware model, both serials, the SIM's
 * numbers, network and services, what the unit can do, and — for cameras —
 * how the firmware numbers its channels. The IMEI itself is not editable:
 * it is how the unit identifies itself on every connection.
 */

const CAPABILITIES: { key: DeviceCapability; label: string; hint: string }[] = [
  { key: 'tracking', label: 'Tracking', hint: 'Reports position; can be a vehicle’s primary unit' },
  { key: 'video', label: 'Cameras (MDVR)', hint: 'Live and recorded video through the MDVR gateway' },
  { key: 'recovery', label: 'Stolen-vehicle recovery', hint: 'Hidden unit; its track is kept separate' },
]

const SIM_SERVICES: { key: SimService; label: string }[] = [
  { key: 'data', label: 'Data' },
  { key: 'sms', label: 'SMS' },
  { key: 'voice', label: 'Voice' },
]

export function DeviceEditor({ device, onClose }: { device: Device; onClose: () => void }) {
  const qc = useQueryClient()
  const models = useQuery({
    queryKey: ['device-models'],
    queryFn: () => api<{ items: DeviceModel[] }>('/device-models'),
  })

  const [form, setForm] = useState({
    modelId: device.modelId ?? '',
    serialNo: device.serialNo ?? '',
    internalSerial: device.internalSerial ?? '',
    phoneNumber: device.phoneNumber ?? '',
    phoneNumber2: device.phoneNumber2 ?? '',
    carrier: device.carrier ?? '',
    apn: device.apn ?? '',
    roaming: device.roaming ?? false,
    simServices: new Set<SimService>(device.simServices?.length ? device.simServices : ['data']),
    capabilities: new Set<DeviceCapability>(device.capabilities?.length ? device.capabilities : ['tracking']),
    notes: device.notes ?? '',
    videoChannels: String(device.videoChannels || 4),
    videoChannelBase: String(device.videoChannelBase ?? 0),
  })
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const hasVideo = form.capabilities.has('video')
  const channels = Number(form.videoChannels)
  const channelsValid = !hasVideo || (Number.isInteger(channels) && channels >= 1 && channels <= 16)

  const save = useMutation({
    mutationFn: () =>
      api(`/devices/${device.id}`, {
        method: 'PATCH',
        body: {
          modelId: form.modelId || undefined,
          serialNo: form.serialNo.trim(),
          internalSerial: form.internalSerial.trim(),
          phoneNumber: form.phoneNumber.trim(),
          phoneNumber2: form.phoneNumber2.trim(),
          carrier: form.carrier.trim(),
          apn: form.apn.trim(),
          roaming: form.roaming,
          // A SIM with nothing ticked is still a SIM: data is the floor.
          simServices: form.simServices.size ? [...form.simServices] : ['data'],
          capabilities: form.capabilities.size ? [...form.capabilities] : ['tracking'],
          notes: form.notes.trim(),
          ...(hasVideo ? { videoChannels: channels, videoChannelBase: Number(form.videoChannelBase) } : {}),
        },
      }),
    onSuccess: () => {
      toast('Device updated', 'ok')
      void qc.invalidateQueries({ queryKey: ['devices'] })
      void qc.invalidateQueries({ queryKey: ['video-info'] })
      onClose()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  function toggle<T extends string>(key: 'simServices' | 'capabilities', value: T) {
    setForm((f) => {
      const next = new Set(f[key] as Set<string>)
      next.has(value) ? next.delete(value) : next.add(value)
      return { ...f, [key]: next }
    })
  }

  const base = Number(form.videoChannelBase)
  const preview = Array.from({ length: channelsValid && hasVideo ? channels : 0 }, (_, i) => `${i + 1}→${base + i}`).join('   ')

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit device ${device.imei}`}
      width={640}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!channelsValid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Hardware */}
        <div className="grid sm:grid-cols-2 gap-3.5">
          <div className="sm:col-span-2">
            <Field label="Hardware model" hint="Determines which protocol decoder runs">
              <select className="field" value={form.modelId} onChange={set('modelId')}>
                <option value="">— unchanged —</option>
                {models.data?.items.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.vendor} {m.model} · {m.protocol}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Serial number" hint="As printed on the unit">
            <input className="field tnum" value={form.serialNo} onChange={set('serialNo')} />
          </Field>
          <Field label="Internal serial" hint="The maker's own number, if different">
            <input className="field tnum" value={form.internalSerial} onChange={set('internalSerial')} />
          </Field>
        </div>

        {/* SIM */}
        <div className="rule-t pt-3.5">
          <div className="label mb-2.5">SIM &amp; network</div>
          <div className="grid sm:grid-cols-2 gap-3.5">
            <Field label="GSM number">
              <input className="field tnum" value={form.phoneNumber} onChange={set('phoneNumber')} placeholder="+27 …" />
            </Field>
            <Field label="Second GSM number" hint="Dual-SIM units">
              <input className="field tnum" value={form.phoneNumber2} onChange={set('phoneNumber2')} />
            </Field>
            <Field label="Network provider">
              <input className="field" value={form.carrier} onChange={set('carrier')} placeholder="Vodacom, MTN, …" />
            </Field>
            <Field label="APN">
              <input className="field" value={form.apn} onChange={set('apn')} placeholder="internet" />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
            <label className="inline-flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={form.roaming} onChange={(e) => setForm((f) => ({ ...f, roaming: e.target.checked }))} />
              Roaming allowed
            </label>
            <div className="inline-flex items-center gap-4">
              <span className="label text-[10px]">Services</span>
              {SIM_SERVICES.map((s) => (
                <label key={s.key} className="inline-flex items-center gap-1.5 text-[13px]">
                  <input type="checkbox" checked={form.simServices.has(s.key)} onChange={() => toggle('simServices', s.key)} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Capabilities */}
        <div className="rule-t pt-3.5">
          <div className="label mb-2.5">What this unit does</div>
          <div className="grid sm:grid-cols-3 gap-2">
            {CAPABILITIES.map((c) => (
              <label
                key={c.key}
                className="flex items-start gap-2 p-2.5 rounded-[3px] cursor-pointer"
                style={{
                  border: `1px solid ${form.capabilities.has(c.key) ? 'var(--accent)' : 'var(--color-rule)'}`,
                  background: form.capabilities.has(c.key) ? 'var(--accent-dim)' : 'transparent',
                }}
              >
                <input type="checkbox" className="mt-0.5" checked={form.capabilities.has(c.key)} onChange={() => toggle('capabilities', c.key)} />
                <span>
                  <span className="block text-[13px]">{c.label}</span>
                  <span className="block text-[11px] text-[var(--color-ink-3)] leading-snug">{c.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Cameras */}
        {hasVideo && (
          <div className="rule-t pt-3.5">
            <div className="label mb-2.5">Cameras</div>
            <div className="grid sm:grid-cols-2 gap-3.5">
              <Field label="Number of cameras" required hint="Shown to people as cameras 1, 2, 3…" error={channelsValid ? undefined : 'Between 1 and 16'}>
                <input className="field tnum" type="number" min={1} max={16} value={form.videoChannels} onChange={set('videoChannels')} />
              </Field>
              <Field label="First channel number" required hint="What the unit's firmware calls camera 1">
                <select className="field" value={form.videoChannelBase} onChange={set('videoChannelBase')}>
                  <option value="0">0 (zero-based)</option>
                  <option value="1">1 (one-based)</option>
                </select>
              </Field>
            </div>
            <div
              className="mt-2.5 px-3 py-2 rounded-[3px] text-[12px] tnum"
              style={{
                color: 'var(--color-ink-2)',
                background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
              }}
            >
              <span className="label text-[10px] mr-2">Camera → channel</span>
              {preview || '—'}
            </div>
          </div>
        )}

        <Field label="Notes">
          <textarea className="field" rows={2} value={form.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  )
}
